import type { JSX } from "react";

import dayjs, { type Dayjs } from "dayjs";
import type { OptionsStackingValue } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import { ColumnChartGraph, formatDate, shortMonthAndDay, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import type { Translate } from "app/filters/i18nFilter";
import {
    TOTAL_KEYWORD_LIST_ID,
    TOTAL_KEYWORD_LIST_COLOR,
    OTHER_KEYWORD_LIST_COLOR,
    useKeywordLists,
} from "app/modules/demand-analysis/keyword-lists";
import DurationServiceSingleton from "app/services/DurationService";

import { ChartTooltipFootnote } from "../../components/ChartTooltipFootnote";
import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../components/ChartTooltipWrapper";
import { formatNumber, formatPercent } from "../../utils/numberFormatters";

import { getSecondaryColorPattern } from "./helpers";
import type { KeywordListData } from "./useKeywordListData";

interface ChartTooltipData {
    metric: string;
    volume: string;
    comparedVolume?: string;
    change: {
        value: string;
        isDecrease: boolean;
    };
}

const getColumnDefinition = (
    translate: Translate,
    previousPeriodTitle: string,
    currentPeriodTitle: string,
    isPeriodOverPeriod: boolean,
) => {
    const columnDefinition: ChartTooltipColumnDefinition<ChartTooltipData>[] = [
        {
            key: "metric",
            title: translate("demand.analysis.search.trends.columnChart.metric.title"),
            align: "left",
            borderRight: true,
            selector: "metric",
            size: "100px",
            render: ({ metric }) => <ChartTooltip.LabelCell label={metric} />,
        },
        {
            key: "comparedVolume",
            title: isPeriodOverPeriod
                ? previousPeriodTitle
                : translate("demand.analysis.search.trends.columnChart.volume.title"),
            align: "right",
            borderRight: true,
            selector: "comparedVolume",
            size: "110px",
            render: ({ comparedVolume }) => (
                <div data-automation="chart-tooltip-value">
                    <ChartTooltip.ValueCell value={comparedVolume} />
                </div>
            ),
        },
        {
            key: "volume",
            title: isPeriodOverPeriod
                ? currentPeriodTitle
                : translate("demand.analysis.search.trends.columnChart.volume.title"),
            align: "right",
            borderRight: true,
            selector: "volume",
            size: "110px",
            render: ({ volume }) => (
                <div data-automation="chart-tooltip-value">
                    <ChartTooltip.ValueCell value={volume} />
                </div>
            ),
        },
        {
            key: "change",
            title: translate(
                isPeriodOverPeriod
                    ? "demand.analysis.search.trends.columnChart.change.compareTitle"
                    : "demand.analysis.search.trends.columnChart.change.title",
            ),
            align: "right",
            borderRight: false,
            selector: "change",
            size: isPeriodOverPeriod ? "70px" : ChartTooltip.ValueCellDefaultwidth,
            render: ({ change }) => (
                <div data-automation="chart-tooltip-value">
                    <ChartTooltip.ChangeCell
                        value={change.value}
                        isDecrease={change.isDecrease}
                    />
                </div>
            ),
        },
    ];

    if (!isPeriodOverPeriod) {
        return columnDefinition.filter(({ selector }) => selector !== "comparedVolume");
    }

    return columnDefinition;
};

type KeywordsTrendsWidgetColumnChartProps = {
    enabledKeywordGroups: Set<string | symbol>;
    height?: number;
    data: KeywordListData;
};

export const KeywordsTrendsWidgetColumnChart = ({
    enabledKeywordGroups,
    height,
    data,
}: KeywordsTrendsWidgetColumnChartProps): JSX.Element | null => {
    const t = useTranslation();
    const { getKeywordListById } = useKeywordLists();
    const isPeriodOverPeriod = DurationServiceSingleton.isPeriodOverPeriod();

    const { legendItems, legendTotal, columnChartData, columnChartTooltipData } = data;
    const enabledLegendItems = [...legendItems, legendTotal].filter((legend) => enabledKeywordGroups.has(legend.id));
    const categories = enabledLegendItems.map((legend) => legend.id);
    const volumeMap = new Map(enabledLegendItems.map((legend) => [legend.id, columnChartTooltipData?.[legend.id]]));
    const chartData = columnChartData?.map((series) => ({
        type: "column",
        name: series.name,
        data: series.data
            .map((datum) => {
                const color =
                    datum.id === TOTAL_KEYWORD_LIST_ID
                        ? TOTAL_KEYWORD_LIST_COLOR
                        : (getKeywordListById(String(datum.id))?.color ?? OTHER_KEYWORD_LIST_COLOR);
                return { ...datum, color: series.isPreviousPeriod ? getSecondaryColorPattern(color) : color };
            })
            .filter((datum) => enabledKeywordGroups.has(String(datum.id))),
    }));
    const [currentPeriodTitle, previousPeriodTitle] =
        columnChartData
            ?.map((entry) => entry.period.map((date) => dayjs(date).format(shortMonthAndYear)).join(" - "))
            .toReversed() ?? [];

    return (
        <ColumnChartGraph
            key={String(enabledLegendItems.length)}
            shouldPresentLegend={false}
            // TODO: Remove `any` once the graph types are fixed {@link https://similarweb-rnd.atlassian.net/browse/PFG-223}
            data={chartData as any}
            graphConfig={{
                chart: {
                    height: enabledKeywordGroups.size === 0 ? 250 : height,
                    width: 160,
                    marginLeft: 16,
                    marginRight: 16,
                    marginBottom: enabledKeywordGroups.size === 0 ? 0 : 5,
                    marginTop: 0,
                },
                plotOptions: {
                    column: {
                        // Highcharts reference doesn't list `null` as allowed value but this is the only way to reset the default library value
                        // TODO: Remove once https://similarweb-rnd.atlassian.net/browse/PFG-251 is resolved
                        stacking: null as unknown as OptionsStackingValue,
                    },
                },
                xAxis: {
                    categories,
                    labels: {
                        enabled: false,
                    },
                    lineWidth: 1,
                    lineColor: colorsPalettes.carbon[100],
                    tickLength: 0,
                    tickWidth: 0,
                    gridLineWidth: 0,
                },
                yAxis: {
                    visible: false,
                },
                tooltip: {
                    outside: true,
                    formatter() {
                        const label = this.options.id!;
                        const keywordList = getKeywordListById(label);
                        const volume = volumeMap.get(label);
                        const title = label === TOTAL_KEYWORD_LIST_ID ? t("common.total") : keywordList?.name;

                        if (!volume) {
                            return "";
                        }

                        let footnote: string | undefined;
                        if (volume.monthToDateStartDate) {
                            const onDate = formatDate(volume.monthToDateStartDate, shortMonthAndYear);
                            const untilDate = (swSettings.current.windowEndDate as Dayjs).format(shortMonthAndDay);
                            footnote = t(
                                "demand_analysis.search_volume_over_time.tooltip.footnote_partial_data.on_date",
                                { onDate, untilDate },
                            );
                        }

                        return renderToStaticMarkup(
                            <div data-automation="column-chart-tooltip-wrapper">
                                <ChartTooltipWrapper<ChartTooltipData>
                                    title={title ?? label}
                                    columnDefinition={getColumnDefinition(
                                        t,
                                        previousPeriodTitle,
                                        currentPeriodTitle,
                                        isPeriodOverPeriod,
                                    )}
                                    data={[
                                        {
                                            metric: t("demand.analysis.search.trends.linechart.volume.title"),
                                            volume: formatNumber(volume.currentVolume),
                                            comparedVolume:
                                                volume.previousPeriodVolume !== undefined
                                                    ? formatNumber(volume.previousPeriodVolume)
                                                    : undefined,
                                            change: {
                                                value: formatPercent(volume.change),
                                                isDecrease: volume.change < 0,
                                            },
                                        },
                                    ]}
                                >
                                    <Stack gap="16px">
                                        {legendTotal.value > 0 ? (
                                            <ChartTooltip.TooltipFooterWithValue
                                                label={t("demand.analysis.search.trends.share_of_out_total")}
                                                value={formatPercent(volume.currentVolume / legendTotal.value)}
                                            />
                                        ) : null}
                                        {footnote ? <ChartTooltipFootnote>{footnote}</ChartTooltipFootnote> : null}
                                    </Stack>
                                </ChartTooltipWrapper>
                            </div>,
                        );
                    },
                },
            }}
        />
    );
};
